import {
  ICheckbookCard,
  IPayoutSettingsResponse
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { listInternalAccounts } from "../../../services/api/banking/internalAccounts";
import { getVerificationMissingData } from "../../../services/api/onboarding/missingFields";
import { selectorAccountFullName } from "../../../shared/selectors/selectorAccountFullName";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { selectorDebitCardFullName } from "../../../shared/selectors/selectorDebitCardFullName";
import { selectorDefaultDestination } from "../../../shared/selectors/selectorDefaultDestination";
import { selectorIsPlatformBalanceAccount } from "../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { getRequireAdditionalInformation } from "../components/FormCreateInternalAccount/utils";
import { PayoutMethod } from "../steps/PayoutMethod";
import {
  OnboardingContext,
  OnboardingModulePayoutMethod,
  OnboardingStep,
  OnboardingStepStatus
} from "../types";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

const getStatus = (
  payoutSettings?: IPayoutSettingsResponse,
  internalAccounts?: Awaited<ReturnType<typeof listInternalAccounts>>,
  bankingMissingData?: Awaited<ReturnType<typeof getVerificationMissingData>>,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): OnboardingStepStatus => {
  const hasPayoutDestination =
    !!payoutSettings && selectorDefaultDestination(payoutSettings);

  const bankingAccount = internalAccounts?.find(
    internalAccount => internalAccount.type === "Banking"
  );

  const hasActiveBankingAccount = bankingAccount?.status === "Active";

  if (hasPayoutDestination || hasActiveBankingAccount) {
    return "Complete";
  }

  const hasPendingBankingAccount = bankingAccount?.status === "Pending";

  if (hasPendingBankingAccount) {
    const requireAdditionalInformation = getRequireAdditionalInformation(
      bankingAccount,
      bankingMissingData,
      featureFlags
    );

    if (requireAdditionalInformation) {
      // Keep going with onboarding, we will ask for additional info
      return "None";
    }

    return "Pending";
  }

  return "None";
};

export const getPayoutMethodStepConfig = (
  _: OnboardingModulePayoutMethod,
  __: OnboardingContext,
  payoutSettings?: IPayoutSettingsResponse,
  accounts?: IAccount[],
  debitCards?: ICheckbookCard[],
  internalAccounts?: Awaited<ReturnType<typeof listInternalAccounts>>,
  bankingMissingData?: Awaited<ReturnType<typeof getVerificationMissingData>>,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): OnboardingStep => {
  const slug = "payout";

  const status = getStatus(
    payoutSettings,
    internalAccounts,
    bankingMissingData,
    featureFlags
  );

  const methodText = getMethodText(
    payoutSettings,
    accounts,
    debitCards,
    internalAccounts
  );

  return {
    title: "Payout method",
    slug,
    component: PayoutMethod,

    status,
    result: {
      Complete: {
        title: "Your payout method was added",
        description: `The payout method was added and your payments will be deposited into your ${methodText}. Click continue for the next step.`
      },
      Pending: {
        title: "Your payout method is almost set up",
        description:
          "Please, go to the Wallet and follow instructions to complete setup"
      }
    }
  };
};

const getMethodText = (
  payoutSettings?: IPayoutSettingsResponse,
  accounts?: IAccount[],
  debitCards?: ICheckbookCard[],
  internalAccounts?: Awaited<ReturnType<typeof listInternalAccounts>>
) => {
  const defaultDestination = payoutSettings
    ? selectorDefaultDestination(payoutSettings)
    : undefined;

  const matchingAccount = accounts?.find(
    account => account.accountId === defaultDestination?.destinationId
  );

  if (matchingAccount) {
    return (
      selectorAccountFullName(matchingAccount) +
      " account via direct deposit (ACH)"
    );
  }

  const matchingDebitCard = debitCards?.find(
    debitCard => debitCard.cardId === defaultDestination?.destinationId
  );

  if (matchingDebitCard) {
    return selectorDebitCardFullName(matchingDebitCard);
  }

  const platformBalanceInternalAccount = payoutSettings
    ? internalAccounts?.find(internalAccount =>
        selectorIsPlatformBalanceAccount(internalAccount, payoutSettings)
      )
    : undefined;

  if (platformBalanceInternalAccount) {
    return "Platform balance";
  }

  const activeBankingAccount = internalAccounts?.find(
    selectorActiveBankingAccount
  );

  if (activeBankingAccount) {
    return "Wingspan Wallet";
  }
};
